import type { RequestFn } from '../http';

/**
 * Zeitvorschläge (v1.404.0): Ein Techniker bucht nicht mehr selbst für einen Kollegen. Wählt er im
 * Cockpit („Buchung als") oder auf der Seite Zeitbuchung einen anderen, legt der Server einen
 * Vorschlag an; der Kollege gibt frei (bucht dann unter seinem Namen), ändert vorher oder verwirft
 * mit Grund. Backend: services/timeProposals/, routes/timeProposals.ts.
 */

export type TimeProposalStatus = 'OPEN' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type TimeProposalKind = 'TICKET' | 'PORTAL';
export type TimeProposalBox = 'received' | 'sent' | 'team';

/** Werte eines Cockpit-Vorschlags (Feldnamen wie POST /weclapp/tickets/:id/time-entries) */
export interface TicketProposalValues {
  hours: number;
  comment: string;
  startDate: number | null;
  articleId: string | null;
  alsoComment: { internal: boolean; public: boolean } | null;
}

/** Werte eines Vorschlags der Seite Zeitbuchung (ohne Techniker) */
export interface PortalProposalValues {
  weclappCustomerId: string;
  summary: string;
  description?: string;
  minutes: number;
  workType?: string;
  billingType?: 'billable' | 'internal' | 'goodwill' | 'non-billable';
  billingPercent?: number | null;
  billingReason?: string;
  billingScope?: string;
  billingScopeReference?: string;
  articleId?: string | null;
  date?: string;
  startTime?: string;
  endTime?: string;
  breakMinutes?: number;
}

export interface TimeProposal {
  id: string;
  kind: TimeProposalKind;
  status: TimeProposalStatus;
  sender: { id: string; name: string | null };
  recipient: { id: string; name: string | null };
  ticket: { id: string; number: string | null; subject: string | null } | null;
  customer: { weclappCustomerId: string; name: string | null } | null;
  startAt: string | null;
  minutes: number;
  comment: string;
  payload: TicketProposalValues | PortalProposalValues;
  edited: boolean;
  rejectReason: string | null;
  decidedAt: string | null;
  decidedBy: { id: string; name: string | null } | null;
  bookingMessage: string | null;
  booking: { ticketTimeBookingId: string | null; portalTimeBookingId: string | null };
  remindedAt: string | null;
  escalatedAt: string | null;
  createdAt: string;
  canApprove: boolean;
  canReject: boolean;
  canCancel: boolean;
}

/** Änderungen beim Freigeben — nur die Felder, die sich geändert haben. */
export type TicketProposalChanges = Partial<Pick<TicketProposalValues, 'hours' | 'startDate' | 'comment' | 'articleId'>>;
export type PortalProposalChanges = Partial<
  Pick<
    PortalProposalValues,
    'summary' | 'description' | 'minutes' | 'date' | 'startTime' | 'endTime' | 'breakMinutes' | 'workType' | 'billingType' | 'billingPercent' | 'billingReason' | 'articleId'
  >
>;

export interface TimeProposalApproveResult {
  success: true;
  status: 'APPROVED';
  edited: boolean;
  /** true: gespeichert, weclapp folgt automatisch */
  pending: boolean;
  message: string | null;
}

export const TIME_PROPOSALS_QUERY_KEY = ['time-proposals'] as const;
export const TIME_PROPOSAL_COUNT_QUERY_KEY = ['time-proposals', 'count'] as const;

export function createTimeProposalsApi(request: RequestFn) {
  return {
    async getTimeProposals(box: TimeProposalBox, open = true): Promise<{ proposals: TimeProposal[] }> {
      return request<{ proposals: TimeProposal[] }>(`/time-proposals?box=${box}&open=${open ? '1' : '0'}`);
    },
    async getTimeProposalCount(): Promise<{ received: number; sent: number }> {
      return request<{ received: number; sent: number }>('/time-proposals/count');
    },
    async approveTimeProposal(
      id: string,
      changes?: TicketProposalChanges | PortalProposalChanges,
    ): Promise<TimeProposalApproveResult> {
      return request<TimeProposalApproveResult>(`/time-proposals/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify(changes && Object.keys(changes).length > 0 ? { changes } : {}),
      });
    },
    async rejectTimeProposal(id: string, reason: string): Promise<{ success: true }> {
      return request<{ success: true }>(`/time-proposals/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },
    async cancelTimeProposal(id: string): Promise<{ success: true }> {
      return request<{ success: true }>(`/time-proposals/${id}/cancel`, { method: 'POST', body: JSON.stringify({}) });
    },
  };
}
